import { useState } from 'react';
import { Link, useNavigate } from 'react-router';
import { useMe } from '../lib/queries';
import { Icon, type IconName } from './ui';
import { Card, IB, Ic, Row, Seg, Sheet, SheetHead, Sp, Top } from './p';

/** The top of a main tab, as drawn: the kitchen on the left, Search and More on the right. */
export function MainHeader() {
  const name = useMe().data?.kitchen.name ?? '';
  return (
    <Top>
      <Link to="/more" className="p-kchip" aria-label={`${name}, more`}>
        <i aria-hidden="true">{name.slice(0, 1).toUpperCase()}</i>
        <span>{name}</span>
      </Link>
      <Sp />
      <IB icon="search" label="Search" to="/search" />
      <IB icon="menu" label="More" to="/more" />
    </Top>
  );
}

export type KitchenView = 'cook' | 'pack' | 'deliver';

/** Kitchen holds the day's whole flow. Cook and Pack live on /kitchen; Deliver is the delivery run. */
export function KitchenTabs({ value, onPick }: { value: KitchenView; onPick?: (v: 'cook' | 'pack') => void }) {
  const nav = useNavigate();
  const pick = (v: KitchenView) => {
    if (v === 'deliver') nav('/delivery');
    else if (onPick) onPick(v);
    else nav(v === 'pack' ? '/kitchen?view=pack' : '/kitchen');
  };
  return <Seg label="Kitchen" value={value} onChange={pick} options={[{ value: 'cook', label: 'Cook' }, { value: 'pack', label: 'Pack' }, { value: 'deliver', label: 'Deliver' }]} />;
}

type CreateItem = { label: string; sub: string; icon: IconName; tone: '' | 's' | 'g' | 'k'; to: string; owner?: boolean };

/** Every "new" thing in one place. Rows join as their features arrive. */
const CREATE: CreateItem[] = [
  { label: 'New order', sub: 'For today or a later day', icon: 'receipt', tone: 'k', to: '/orders/new' },
  { label: 'New subscription', sub: 'A monthly plan, or any length', icon: 'people', tone: 's', to: '/new' },
  { label: 'Record a payment', sub: 'Cash or UPI, from anyone who owes', icon: 'rupee', tone: 'g', to: '/revenue', owner: true },
  { label: 'Import customers', sub: 'From Excel or a list you keep', icon: 'sheet', tone: '', to: '/import' },
];

/** The saffron ＋ above the bar, and the Create sheet it opens. */
export function CreateButton({ to, label = 'Create' }: { to?: string; label?: string } = {}) {
  const [open, setOpen] = useState(false);
  const owner = useMe().data?.user.role === 'owner';
  const items = CREATE.filter((c) => !c.owner || owner);
  // On a tab with one obvious "new" (Subscribers), the ＋ goes straight there.
  if (to) return <Link className="fab" to={to} aria-label={label}><Icon name="plus" size={24} sw={2.3} /></Link>;
  return (
    <>
      <button type="button" className="fab" aria-label="Create" onClick={() => setOpen(true)}>
        <Icon name="plus" size={24} sw={2.3} />
      </button>
      {open && (
        <Sheet label="Create" onClose={() => setOpen(false)}>
          <SheetHead kick="What would you like to add?" title="Create" onClose={() => setOpen(false)} />
          <Card>
            {items.map((c) => <Row key={c.label} to={c.to} left={<Ic icon={c.icon} tone={c.tone} size={18} />} t={c.label} s={c.sub} />)}
          </Card>
        </Sheet>
      )}
    </>
  );
}
